/**
 * onboardingOptions.ts — the single source for the onboarding work-area and
 * capability catalogs (moved out of Onboarding.tsx 2026-08-04).
 *
 * SHARED between the onboarding page and the recommendation PREFETCH fired
 * at code-redemption time (src/services/onboardingRecsPrefetch.ts). The
 * server caches recommendations under a hash of the firm profile + these
 * lists — both callers must send byte-identical lists for the prefetch to
 * warm the cache the profile page later reads.
 */

// Persisted under the existing `practiceAreas` key so downstream readers
// (tenant profile, VM onboarding profile, memory docs) keep working unchanged.
// (tenant profile, VM onboarding profile, memory docs) keep working unchanged.
export const DEFAULT_PRACTICE_OPTIONS = [
  'Legal',
  'Finance & Accounting',
  'Marketing & Content',
  'Sales & Business Development',
  'Operations & Admin',
  'Customer Support',
  'Real Estate',
  'Healthcare',
  'Recruiting & HR',
  'Consulting & Professional Services',
  'Software & Product',
  'E-commerce & Retail',
  'Education & Training',
  'Insurance',
  'Construction & Trades',
  'Media & Creative',
  'Nonprofit & Government',
  'Other',
];

// ── Dynamic capabilities keyed by work area ──
export const CAPABILITIES_BY_PRACTICE: Record<string, string[]> = {
  Legal: ['Deep Legal Research & Case Law Synthesis', 'Motion, Brief & Demand Letter Drafting', 'Contract & NDA Drafting/Review', 'Government & Court Form Filling', 'Client Intake in Spanish & English', 'Case Workflow Automation'],
  'Finance & Accounting': ['Financial Report Drafting & Analysis', 'Spreadsheet & Data Modeling', 'Invoice & Billing Workflows', 'Regulatory & Compliance Research', 'Market & Company Research', 'Audit Documentation Prep'],
  'Marketing & Content': ['Blog & Article Writing', 'Social Media Content & Scheduling', 'Email Campaign Drafting', 'Video Script Generation', 'SEO & Market Research', 'Brand Voice Consistency'],
  'Sales & Business Development': ['Lead Research & Qualification', 'Outreach & Follow-up Drafting', 'Proposal & Quote Generation', 'CRM Updates & Pipeline Tracking', 'Competitor Intelligence', 'Meeting Prep & Summaries'],
  'Operations & Admin': ['Email & Calendar Management', 'Document Organization & Filing', 'Process Documentation', 'Data Entry & Cleanup', 'Vendor & Order Coordination', 'Meeting Notes & Action Items'],
  'Customer Support': ['Ticket Triage & Draft Replies', 'Knowledge Base Articles', 'Bilingual Customer Communication', 'Escalation Summaries', 'FAQ & Help Content Generation'],
  'Real Estate': ['Listing Descriptions & Marketing', 'Lease & Purchase Agreement Review', 'Property & Market Research', 'Closing Document Prep', 'Client Communication & Scheduling'],
  Healthcare: ['Patient Communication Drafting', 'Medical Record Summarization', 'HIPAA-Aware Documentation', 'Insurance & Claims Paperwork', 'Regulatory Compliance Research'],
  'Recruiting & HR': ['Job Description Drafting', 'Resume Screening & Summaries', 'Interview Scheduling & Prep', 'Onboarding Document Creation', 'Policy & Handbook Drafting'],
  'Consulting & Professional Services': ['Client Research & Briefings', 'Proposal & SOW Drafting', 'Slide & Report Preparation', 'Meeting Summaries & Follow-ups', 'Industry & Market Analysis'],
  'Software & Product': ['Product Spec & PRD Drafting', 'User Research Summaries', 'Release Notes & Documentation', 'Competitive Analysis', 'Customer Feedback Triage'],
  'E-commerce & Retail': ['Product Description Writing', 'Order & Inventory Workflows', 'Customer Service Replies', 'Marketplace Listing Optimization', 'Promotion & Campaign Content'],
  'Education & Training': ['Course & Lesson Material Creation', 'Training Documentation', 'Student/Parent Communication', 'Grading Rubrics & Feedback Drafts', 'Educational Video Scripts'],
  Insurance: ['Policy Review & Summaries', 'Claims Documentation & Research', 'Client Communication Drafting', 'Coverage Comparison Research', 'Compliance Documentation'],
  'Construction & Trades': ['Estimate & Proposal Drafting', 'Permit Research & Applications', 'Project Documentation', 'Client & Subcontractor Communication', 'Safety & Compliance Docs'],
  'Media & Creative': ['Script & Story Development', 'Content Calendar Planning', 'Pitch & Treatment Drafting', 'Audience & Trend Research', 'Production Coordination'],
  'Nonprofit & Government': ['Grant Research & Writing', 'Donor & Constituent Communication', 'Program Documentation', 'Compliance & Reporting Prep', 'FOIA & Public Records Requests'],
  Other: ['Custom Research Workflows', 'Document Drafting & Review', 'Email & Communication Management', 'Data Organization & Reporting'],
};

// Universal capabilities available to every work area. Deliberately broad
// (2026-08-02 owner directive): the default view should already convey range,
// and the surrounding copy makes clear this is a SAMPLE — prest0n handles far
// more and keeps learning new skills for each business.
export const UNIVERSAL_CAPABILITIES = [
  'Deep Research & Analysis',
  'Document Reading & Multi-File Analysis (up to 50 files)',
  'Document Drafting & Review',
  'Bilingual Translation (English ↔ Spanish)',
  'Client Communication Drafting',
  'Video Script Creation (Educational & Marketing)',
  'Email & Calendar Management',
  'Meeting Notes, Summaries & Follow-ups',
  'Proposal, Report & Presentation Drafting',
  'Data Organization & Reporting',
  'Web Research & Ongoing Monitoring',
  'Recurring Routines & Workflow Automations',
];

export function getCapabilitiesForAreas(areas: string[]): string[] {
  const specific = new Set<string>();
  for (const area of areas) {
    const caps = CAPABILITIES_BY_PRACTICE[area];
    if (caps) caps.forEach(c => specific.add(c));
  }
  return [...UNIVERSAL_CAPABILITIES, ...Array.from(specific)];
}


/** The full capability universe — the exact list the recommendations
 *  endpoint validates against. One implementation for every caller. */
export function buildCapabilityUniverse(): string[] {
  return Array.from(new Set([
    ...UNIVERSAL_CAPABILITIES,
    ...Object.values(CAPABILITIES_BY_PRACTICE).flat(),
  ]));
}

// ── Capability GROUPS (task #53, owner decision-fatigue report 2026-08-12) ──
// The profile page used to render every tailored capability as its own chip —
// 10–18 decisions on one screen. These groups are a DISPLAY LAYER ONLY: the
// stored model stays the individual capability strings (server
// recommendations validate against buildCapabilityUniverse(); tenant config,
// memory docs, and the prefetch hash all consume the raw list — none of that
// changes). One bubble toggles all of its member capabilities, and the
// members double as the idea-generation examples inside the bubble.

export interface CapabilityGroup {
  key: string;
  label: string;
  /** First group whose pattern matches OWNS the capability — order matters. */
  match: RegExp;
}

export const CAPABILITY_GROUPS: CapabilityGroup[] = [
  { key: 'bilingual', label: 'bilingual & translation', match: /bilingual|spanish|translat/i },
  { key: 'clients', label: 'clients & customers', match: /client|customer|patient|donor|constituent|intake|ticket|student|parent|subcontractor/i },
  { key: 'inbox', label: 'email, calendar & meetings', match: /email|calendar|meeting|schedul|outreach|follow-up/i },
  { key: 'research', label: 'research & analysis', match: /research|analysis|intelligence|monitor|screening|comparison|trend|feedback/i },
  { key: 'data', label: 'data, sheets & billing', match: /spreadsheet|data|invoice|billing|modeling|inventory|order|reporting|crm|pipeline/i },
  { key: 'documents', label: 'documents & files', match: /document|file|form|contract|nda|policy|handbook|permit|paperwork|claims|compliance|record|lease|agreement|closing|audit/i },
  { key: 'writing', label: 'writing & content', match: /draft|writ|content|script|blog|article|social|seo|brand|description|listing|proposal|quote|grant|pitch|story|course|lesson|rubric|notes|resume|summar|video|campaign|marketing|treatment|prep/i },
  { key: 'automation', label: 'automations & routines', match: /workflow|automation|routine|process|triage|coordination|cleanup|entry|organization|management|optimization/i },
  // Catch-all keeps the mapping total — nothing silently disappears.
  { key: 'more', label: 'other ways to help', match: /./ },
];

/** Partition a capability list into the display groups, preserving each
 *  group's curated order and each capability's original order. Groups with
 *  no members are dropped. */
export function groupCapabilities(
  caps: string[],
): Array<{ key: string; label: string; members: string[] }> {
  const byKey = new Map<string, string[]>();
  for (const cap of caps) {
    const group = CAPABILITY_GROUPS.find((g) => g.match.test(cap)) as CapabilityGroup;
    (byKey.get(group.key) ?? byKey.set(group.key, []).get(group.key)!).push(cap);
  }
  return CAPABILITY_GROUPS
    .filter((g) => (byKey.get(g.key) || []).length > 0)
    .map((g) => ({ key: g.key, label: g.label, members: byKey.get(g.key)! }));
}

/** Keyword → work-area map for the INSTANT pre-selection layer (2026-08-04):
 *  derives areas from the invite's industry/focus text at first paint, before
 *  the model recommendations land. First match wins; max two applied. */
export const INDUSTRY_AREA_KEYWORDS: Array<[RegExp, string]> = [
  [/law|legal|attorney|immigration|estate plan|probate|paralegal/i, 'Legal'],
  [/account|bookkeep|financ|tax|audit|payroll/i, 'Finance & Accounting'],
  [/market|advertis|brand|content|creative agency|social media/i, 'Marketing & Content'],
  [/sales|business development|lead gen/i, 'Sales & Business Development'],
  [/logistics|freight|shipping|supply chain|operations|admin/i, 'Operations & Admin'],
  [/customer support|customer service|help desk/i, 'Customer Support'],
  [/real estate|property|brokerage|leasing/i, 'Real Estate'],
  [/health|medical|dental|clinic|patient|care/i, 'Healthcare'],
  [/recruit|staffing|human resources|\bhr\b/i, 'Recruiting & HR'],
  [/consult|advisory|professional services/i, 'Consulting & Professional Services'],
  [/software|product|saas|engineering|tech startup/i, 'Software & Product'],
  [/e-?commerce|retail|store|marketplace/i, 'E-commerce & Retail'],
  [/educat|school|training|course|tutor/i, 'Education & Training'],
  [/insurance|claims|underwrit/i, 'Insurance'],
  [/construction|architect|contractor|trades|builder|permit/i, 'Construction & Trades'],
  [/media|film|studio|production|publish/i, 'Media & Creative'],
  [/nonprofit|government|ngo|grant/i, 'Nonprofit & Government'],
];
